/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
  DatePicker,
} from 'antd';
import FormSubmitControls from 'components/FormSubmitControls';
import Table from 'components/Table';
import { onError } from 'utils/handlers';
import { DATE_FRONTEND_FORMAT, dateFormatterToReporteador } from 'utils/formatValues';
import Select from 'components/Select';
import { FilePdfOutlined } from '@ant-design/icons';
import { postObtenerReporte } from 'api/reporteador';
import Previewer, { downloadBase64 } from 'components/Previewer';
import Moment from 'moment';
import usePermissionMessage from 'hooks/usePermissionMessage';
import { getSistemas } from 'api/recaudacion/sistemas';
import { getUnidadesDeRecaudacion } from 'api/configuracion/unidadesDeRecaudacion';
import { getTiposDeCargosLite } from 'api/recaudacion/tiposDeCargo';
import { getPeriodosFiscales } from 'api/catalogos/periodosFiscales';
import { getConceptosDeIngresosLite } from 'api/catalogos/conceptosDeIngresos';
import getAllPages from 'utils/getAllPages';
import axios from 'axios';

const data = [
  {
    id: 133,
    nombre: 'Pagos',
  },
];

const reportesInactivos = [
  {
    id: 17,
    nombre: 'Descuentos por Tipo',
  },
  {
    id: 27,
    nombre: 'Sumario de Contabilidad',
  },
  {
    id: 10021,
    nombre: 'Auxiliar Detallado de Captacion',
  },
  {
    id: 10027,
    nombre: 'Estadística de Recibos',
  },
  {
    id: 10038,
    nombre: 'Concentrado de Ingresos Global',
  },
  {
    id: 24,
    nombre: 'Tipo de cargo pagado con observaciones',
  },
  {
    id: 19,
    nombre: 'Tipo de Cargo Pagado/Abonado',
  },
  {
    id: 28,
    nombre: 'Conceptos de ingresos pagados',
  },
];

export const permissionConfig = {
  mainPermission: 'view_reportesrecaudacion',
  required: ['view_unidadderecaudacion'],
};

const formatos = ['pdf', 'excel'];

const fechaFieldNamesPorReporte = {
  17: ['fechainicial', 'fechafinal'],
  133: ['fechainicio', 'fechafinal'],
};

const ReportesDeRecaudacion = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visiblePreviewer, setVisiblePreviewer] = useState(false);
  const [base64, setBase64] = useState('');
  const [sistemas, setSistemas] = useState([]);
  const [unidadDeRecaudacion, setUnidadDeRecaudacion] = useState([]);
  const [periodosFiscales, setPeriodosFiscales] = useState([]);
  const [conceptosDeIngresos, setConceptosDeIngresos] = useState([]);
  const [loadingConceptosDeIngresos, setLoadingConceptosDeIngresos] = useState(true);
  const [tiposDeCargos, setTiposDeCargos] = useState([]);
  const [loadingTiposDeCargos, setLoadingTiposDeCargos] = useState(true);

  usePermissionMessage(permission);

  useEffect(() => {
    let mounted = true;
    const source = axios.CancelToken.source();

    const fetchConceptosDeIngresos = async () => {
      const _data = await getAllPages(getTiposDeCargosLite, { page_size: 50 }, source.token);
      if (mounted) {
        setTiposDeCargos(_data);
        setLoadingTiposDeCargos(false);
      }
    };

    fetchConceptosDeIngresos();
    return () => {
      source.cancel();
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const source = axios.CancelToken.source();

    const fetchTiposDeCargos = async () => {
      const _data = await getAllPages(getConceptosDeIngresosLite, { page_size: 50 }, source.token);
      if (mounted) {
        setConceptosDeIngresos(_data);
        setLoadingConceptosDeIngresos(false);
      }
    };

    fetchTiposDeCargos();
    return () => {
      source.cancel();
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const [_sistemas, _unidades, _periodos] = await Promise.all([
        getSistemas(),
        getUnidadesDeRecaudacion(),
        getPeriodosFiscales(),
      ]);
      _unidades.sort((a, b) => a.descripcion.localeCompare(b.descripcion));
      _unidades.unshift({ id: -1, descripcion: 'Seleccionar todos' }, { id: 0, descripcion: 'Eliminar selección' });
      if (mounted) {
        setSistemas(_sistemas);
        setUnidadDeRecaudacion(_unidades);
        setPeriodosFiscales(_periodos);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setBase64('');
  };

  const formatPostValues = (ID, values) => {
    const keys = Object.keys(values);
    const Parametros = keys.map((e) => {
      const fields = {
        fecha_inicial: () => dateFormatterToReporteador(values[e]),
        fecha_final: () => dateFormatterToReporteador(values[e], false),
        fechainicial: () => dateFormatterToReporteador(values[e]),
        fechainicio: () => dateFormatterToReporteador(values[e]),
        fechafinal: () => dateFormatterToReporteador(values[e], false),
      };
      const dataType = {
        fecha_inicial: 'Fecha',
        fecha_final: 'Fecha',
        fechainicial: 'Fecha',
        fechainicio: 'Fecha',
        fechafinal: 'Fecha',
        sistema_id: 'Entero',
        unidadesrecaudacion: 'Texto',
        tipodecargo: 'Texto',
      };
      const Valor = (fields[e] && fields[e]()) || values[e] || null;
      return {
        Nombre: e,
        TipoParametro: dataType[e],
        Valor,
      };
    }).filter((e) => e.Valor !== null);
    return { Parametros, ID };
  };

  const nombreReporte = data.find((item) => item.id === selectedRowKeys[0])?.nombre;

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const { tipo, ..._values } = form.getFieldsValue();
      const [key] = selectedRowKeys;
      if (_values.fechas) {
        const [fechaInicioKey, fechaFinalKey] = fechaFieldNamesPorReporte[key] || ['fecha_inicial', 'fecha_final'];
        [_values[fechaInicioKey], _values[fechaFinalKey]] = _values.fechas;
      }
      delete _values.fechas;

      if ([19, 24, 28].includes(key)) {
        _values.tipodecargo = _values.tipodecargo.join(', ');
      }
      if ([27, 10038].includes(key)) {
        _values.unidadesrecaudacion = _values.unidadesrecaudacion.join(', ');
      }
      const values = formatPostValues(key, _values);
      const result = await postObtenerReporte(values, tipo);
      if (result) {
        onCancel();
        setBase64(result);
        if (tipo === 'excel') {
          downloadBase64(result, `${nombreReporte}.xlsx`);
        } else {
          setVisiblePreviewer(true);
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickConsultar = () => {
    form.resetFields();
    setVisible(true);
  };

  const rules = {
    required: [{ required: true }],
  };

  const columns = [
    {
      titleText: 'Nombre del reporte',
      dataIndex: 'nombre',
      key: 'nombre',
      width: 350,
    },
  ];

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          customActions={[{
            icon: <FilePdfOutlined />,
            text: 'Consultar',
            onClick: onClickConsultar,
            hasPermission: permission.hasAllViewPermission,
          }]}
          rowKey="id"
          mobileColIndex={0}
          permission={permission}
        />
        <Modal
          open={visible}
          loading={loading}
          footer={null}
          closable={false}
          maskClosable={false}
          title={(
            <FormSubmitControls
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              label="Consulta de Reporte"
              onFinishText="Ejecutar"
            />
          )}
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={() => onFinish()}
            scrollToFirstError
            initialValues={{ tipo: formatos[0] }}
          >
            <Row gutter={10}>
              <Col span={24}>
                <Form.Item
                  label="Fecha Inicial y Final"
                  rules={rules.required}
                  name="fechas"
                >
                  <DatePicker.RangePicker
                    placeholder=""
                    format={DATE_FRONTEND_FORMAT}
                    disabledDate={(current) => current && current > Moment().endOf('day')}
                    disabled={loading}
                  />
                </Form.Item>
              </Col>
              {selectedRowKeys[0] === 10021 && (
                <Col span={24}>
                  <Form.Item
                    label="Sistema"
                    rules={rules.required}
                    name="sistema_id"
                  >
                    <Select dataSource={sistemas} disabled={loading} />
                  </Form.Item>
                </Col>
              )}
              {[27, 10038].includes(selectedRowKeys[0]) && (
                <Col span={24}>
                  <Form.Item
                    label="Unidad de Recaudación"
                    name="unidadesrecaudacion"
                  >
                    <Select
                      dataSource={unidadDeRecaudacion}
                      disabled={loading}
                      mode="multiple"
                      onChange={(selected) => {
                        if (selected.includes(0)) {
                          form.setFieldValue('unidadesrecaudacion', []);
                        }
                        if (selected.includes(-1)) {
                          form.setFieldValue('unidadesrecaudacion', unidadDeRecaudacion.map((e) => e.id).slice(2));
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
              )}
              {[24, 19].includes(selectedRowKeys[0]) && (
                <Col span={24}>
                  <Form.Item
                    name="tipodecargo"
                    label="Tipo de Cargo"
                    hasFeedback
                  >
                    <Select
                      dataSource={tiposDeCargos}
                      render={(record) => `${periodosFiscales
                        .find((e) => e.id === record.periodo_fiscal)?.periodo} - ${record.descripcion}`}
                      mode="multiple"
                      loading={loadingTiposDeCargos}
                    />
                  </Form.Item>
                </Col>
              )}
              {[28].includes(selectedRowKeys[0]) && (
                <Col span={24}>
                  <Form.Item
                    name="tipodecargo"
                    label="Concepto de ingreso"
                    hasFeedback
                  >
                    <Select
                      dataSource={conceptosDeIngresos}
                      render={(record) => `${periodosFiscales
                        .find((e) => e.id === record.periodo_fiscal)?.periodo} - ${record.descripcion}`}
                      mode="multiple"
                      loading={loadingConceptosDeIngresos}
                    />
                  </Form.Item>
                </Col>
              )}
              <Col span={24}>
                <Form.Item
                  label="Formato del Reporte"
                  rules={rules.required}
                  name="tipo"
                  tooltip="Elegir entre pdf y excel"
                >
                  <Select dataSource={formatos} disabled={loading} />
                </Form.Item>
              </Col>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Row>
          </Form>
        </Modal>
      </Spin>
      <Previewer
        visible={visiblePreviewer}
        base64={base64}
        setVisible={(value) => {
          setVisiblePreviewer(value);
          onCancel();
        }}
        allowSend
        isImage={false}
        allowDownload
        fileName={data.find((item) => item.id === selectedRowKeys[0])?.nombre}
      />
    </Row>
  );
};

export default ReportesDeRecaudacion;
